import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TBranchReduxState } from '~types/redux/state';
import {
    TBranch,
    TBranchFilter,
    TCreateNewBranchReduxParams,
    TCreateNewRegionReduxParams,
    TEditBranchDetailsReduxParams,
    TEditRegionDetailsReduxParams,
    TGetBranchListingAPIResponse,
    TGetBranchListingFilters,
    TGetRegionListingAPIResponse,
    TRegion,
    TRegionFilter,
} from '~types/feat/branch';

const initialState: TBranchReduxState = {
    loadings: {
        getBranchListing: false,
        getRegionListing: false,
        createNewRegion: false,
        createNewBranch: false,
        getAllRegions: false,
        getAllBranches: false,
        editBranchDetails: false,
        editRegionDetails: false,
    },
    modals: {
        createNewBranchModalIsOpen: false,
        createNewRegionModalIsOpen: false,
        branchDetailsModalIsOpen: false,
        deleteBranchModalIsOpen: false,
        isEditing: false,
        regionDetailsModalIsOpen: false,
        regionIsEditing: false,
        deleteRegionModalIsOpen: false,
    },
    regionListing: {
        index: 0,
        maxIndex: 0,
        data: [],
    },
    branchListing: {
        index: 0,
        maxIndex: 0,
        filters: {
            regionName: '',
        },
        data: [],
    },
    selectedBranch: undefined,
    selectedRegion: undefined,
    allBranches: [],
    allRegions: [],
    errors: {
        getBranchListing: '',
        getRegionListing: '',
        createNewRegion: '',
        createNewBranch: '',
        getAllRegions: '',
        getAllBranches: '',
        editBranchDetails: '',
        editRegionDetails: '',
    },
};

const branchSlice = createSlice({
    name: 'branch',
    initialState,
    reducers: {
        branchSetCreateNewRegionModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createNewRegionModalIsOpen = action.payload;
        },
        branchSetRegionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.regionListing.index = action.payload.index;
        },
        branchGetRegionListingAttempt: (state) => {
            state.loadings.getRegionListing = true;
            state.errors.getRegionListing = '';
        },
        branchGetRegionListingSuccess: (state, action: PayloadAction<TGetRegionListingAPIResponse>) => {
            state.loadings.getRegionListing = false;
            state.regionListing = action.payload;
        },
        branchGetRegionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getRegionListing = false;
            if (action.payload) state.errors.getRegionListing = action.payload;
        },
        branchSetRegionDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.regionDetailsModalIsOpen = action.payload;
        },
        branchSetDeleteRegionModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.deleteRegionModalIsOpen = action.payload;
        },
        branchResetEditRegionDetailsState: (state) => {
            state.loadings.editRegionDetails = initialState.loadings.editRegionDetails;
            state.errors.editRegionDetails = initialState.errors.editRegionDetails;
        },
        branchEditRegionDetailsAttempt: (state, _action: PayloadAction<TEditRegionDetailsReduxParams>) => {
            state.loadings.editRegionDetails = true;
            state.errors.editRegionDetails = '';
        },
        branchEditRegionDetailsSuccess: (state) => {
            state.loadings.editRegionDetails = false;
        },
        branchEditRegionDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.editRegionDetails = false;
            if (action.payload) state.errors.getAllBranches = action.payload;
        },
        branchSetSelectedRegion: (state, action: PayloadAction<TRegion | undefined>) => {
            state.selectedRegion = action.payload;
        },
        branchSetRegionDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.regionIsEditing = action.payload;
        },
        branchSetCreateNewBranchModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createNewBranchModalIsOpen = action.payload;
        },
        branchSetDeleteBranchModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.deleteBranchModalIsOpen = action.payload;
        },
        branchSetBranchDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.branchDetailsModalIsOpen = action.payload;
        },
        branchSetBranchDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.isEditing = action.payload;
        },
        branchResetEditBranchDetailsState: (state) => {
            state.loadings.editBranchDetails = initialState.loadings.editBranchDetails;
            state.errors.editBranchDetails = initialState.errors.editBranchDetails;
        },
        branchEditBranchDetailsAttempt: (state, _action: PayloadAction<TEditBranchDetailsReduxParams>) => {
            state.loadings.editBranchDetails = true;
            state.errors.editBranchDetails = '';
        },
        branchEditBranchDetailsSuccess: (state) => {
            state.loadings.editBranchDetails = false;
        },
        branchEditBranchDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.editBranchDetails = false;
            if (action.payload) state.errors.getAllBranches = action.payload;
        },
        branchSetSelectedBranch: (state, action: PayloadAction<TBranch | undefined>) => {
            state.selectedBranch = action.payload;
        },
        branchSetGetBranchListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.branchListing.index = action.payload.index;
        },
        branchSetGetBranchListingFilters: (state, action: PayloadAction<TGetBranchListingFilters>) => {
            state.branchListing.filters = action.payload;
        },
        branchClearGetBranchListingFilters: (state) => {
            state.branchListing.filters = initialState.branchListing.filters;
        },
        branchGetBranchListingAttempt: (state) => {
            state.loadings.getBranchListing = true;
            state.errors.getBranchListing = '';
        },
        branchGetBranchListingSuccess: (state, action: PayloadAction<TGetBranchListingAPIResponse>) => {
            state.loadings.getBranchListing = false;
            state.branchListing = {
                filters: state.branchListing.filters,
                ...action.payload,
            };
        },
        branchGetBranchListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getBranchListing = false;
            if (action.payload) state.errors.getBranchListing = action.payload;
        },
        branchCreateNewRegionAttempt: (state, _action: PayloadAction<TCreateNewRegionReduxParams>) => {
            state.loadings.createNewRegion = true;
            state.errors.createNewRegion = '';
        },
        branchCreateNewRegionSuccess: (state) => {
            state.loadings.createNewRegion = false;
        },
        branchCreateNewRegionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createNewRegion = false;
            if (action.payload) state.errors.createNewRegion = action.payload;
        },
        branchResetCreateNewRegionState: (state) => {
            state.loadings.createNewRegion = initialState.loadings.createNewRegion;
            state.errors.createNewRegion = initialState.errors.createNewRegion;
        },
        branchCreateNewBranchAttempt: (state, _action: PayloadAction<TCreateNewBranchReduxParams>) => {
            state.loadings.createNewBranch = true;
            state.errors.createNewBranch = '';
        },
        branchCreateNewBranchSuccess: (state) => {
            state.loadings.createNewBranch = false;
        },
        branchCreateNewBranchFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createNewBranch = false;
            if (action.payload) state.errors.createNewBranch = action.payload;
        },
        branchResetCreateNewBranchState: (state) => {
            state.loadings.createNewBranch = initialState.loadings.createNewBranch;
            state.errors.createNewBranch = initialState.errors.createNewBranch;
        },
        branchGetAllRegionsAttempt: (state) => {
            state.loadings.getAllRegions = true;
            state.errors.getAllRegions = '';
        },
        branchGetAllRegionsSuccess: (state, action: PayloadAction<TRegionFilter[]>) => {
            state.loadings.getAllRegions = false;
            state.allRegions = action.payload;
        },
        branchGetAllRegionsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllRegions = false;
            if (action.payload) state.errors.getAllRegions = action.payload;
        },
        branchGetAllBranchesAttempt: (state) => {
            state.loadings.getAllBranches = true;
            state.errors.getAllBranches = '';
        },
        branchGetAllBranchesSuccess: (state, action: PayloadAction<TBranchFilter[]>) => {
            state.loadings.getAllBranches = false;
            state.allBranches = action.payload;
        },
        branchGetAllBranchesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllBranches = false;
            if (action.payload) state.errors.getAllBranches = action.payload;
        },
    },
});

export default {
    actions: branchSlice.actions,
    reducers: branchSlice.reducer,
};
